import React, { useEffect, useState, useContext } from "react";

import { auth, firestore } from "./firebase";
import { calculateProfileUpdate } from "../schema";

import {
  countryOptions,
  jobFieldOptions,
  majorOptions,
  degreeOptions,
  kvisYearOptions,
  uniNameOptions
} from "../schema";

const fields = [
  { name: "country", options: countryOptions },
  { name: "jobField", options: jobFieldOptions },
  { name: "major", options: majorOptions },
  { name: "degree", options: degreeOptions },
  { name: "kvisYear", options: kvisYearOptions },
  { name: "uniName", options: uniNameOptions }
];

const UserContext = React.createContext({ user: null, profile: null });

const useUser = () => useContext(UserContext);

const labelCompare = (a, b) => {
  const labelA = a.label.toLowerCase();
  const labelB = b.label.toLowerCase();
  if (labelA < labelB) return -1;
  if (labelA > labelB) return 1;
  return 0;
}

// const INTERVAL = 24 * 60 * 60 * 1000; // 1-day
const INTERVAL = 60 * 60 * 1000; // 1-hour

const KVIS_SIGNIN_PENDING = "kvis-signin-pending";
const KVIS_SIGNIN_REDIRECT = "kvis-signin-redirect";

const makeQuerablePromise = (promise) => {
  if (promise.isPending) return promise;

  var isPending = true;
  var result = promise.then(v => {
    isPending = false;
    return v;
  }, e => {
    isPending = false;
    throw e;
  })
  result.isPending = () => isPending;
  return result;
}

const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState({
    kvisYear: {},
    country: {},
    state: {},
    jobField: {},
    major: {},
    degree: {},
    employer: {},
    jobTitle: {},
    uniName: {}
  });
  const [refresh, setRefresh] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [options, setOptions] = useState(fields.reduce((acc, val) => {
    acc[val.name] = val.options;
    return acc;
  }, {}));

  const [signInPending, setSignInPending] = useState(makeQuerablePromise(Promise.resolve()));
  useEffect(() => {
    if (window.sessionStorage.getItem(KVIS_SIGNIN_PENDING)) {
      setSignInPending(makeQuerablePromise((async () => {
        window.sessionStorage.removeItem(KVIS_SIGNIN_PENDING);
        const result = await auth().getRedirectResult();

        if (result.additionalUserInfo.isNewUser) {
          await firestore()
            .collection("users")
            .doc(result.user.uid)
            .set({
              firstName: result.additionalUserInfo.profile.givenName,
              lastName: result.additionalUserInfo.profile.surname
            });
        }
      })()));
    }
  }, []);
  const signInWithKvis = async () => {
    const provider = new auth.OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: "e9c554b7-2aec-4238-9b8e-372753d596ae"
    });

    try {
      window.sessionStorage.setItem(KVIS_SIGNIN_PENDING, 1);
      if (!window.sessionStorage.getItem(KVIS_SIGNIN_REDIRECT))
        window.sessionStorage.setItem(KVIS_SIGNIN_REDIRECT, "/edit");
      await auth().signInWithRedirect(provider);
    } catch (error) {
      alert(error.message);
    }
  };

  useEffect(() => auth().onAuthStateChanged(newUser => {
    (async () => {
      await signInPending;
      if (newUser) {
        const docRef = firestore()
          .collection("users")
          .doc(newUser.uid);
        const doc = await docRef.get();
        if (!doc.exists) {
          window.sessionStorage.setItem(KVIS_SIGNIN_REDIRECT, "/edit");
          await docRef.set({});
        }
      }
      setUser(newUser);
    })();
  }), [setUser, refresh, signInPending]);
  useEffect(
    () => {
      if (user && user.uid) {
        return firestore()
          .collection("users")
          .doc(user.uid)
          .onSnapshot(snapshot => {
            setProfile(snapshot.data());
          });
      }
    },
    [user, setProfile]
  );
  useEffect(() => {
    const fetchData = async () => {
      setSummaryLoading(true);

      const metaSnapshot = await firestore()
        .doc("summary/meta")
        .get();
      const meta = metaSnapshot.data();
      const summarySnapshot = await firestore()
        .doc("summary/summary")
        .get();
      const summary_ = summarySnapshot.data();
      if (summary_ && meta && meta.lastUpdated) {
        setSummary({
          ...summary_,
          lastUpdated: meta.lastUpdated
        });
        setSummaryLoading(false);
      }
      if (
        !meta ||
        !meta.lastUpdated ||
        Date.now() - meta.lastUpdated >= INTERVAL
      ) {
        if (!meta || !meta.inProgress) {
          await fetch(
            "https://us-central1-kvis-alumni.cloudfunctions.net/updateSummary"
          );
          const newSummarySnapshot = await firestore()
            .doc("summary/summary")
            .get();
          const newMetaSnapshot = await firestore()
            .doc("summary/meta")
            .get();

          const newSummary_ = newSummarySnapshot.data();
          const newMeta_ = newMetaSnapshot.data();
          if (newSummary_ && newMeta_) {
            setSummary({
              ...newSummary_,
              lastUpdated: newMeta_.lastUpdated
            });
          }
        }
      }
      setSummaryLoading(false);
    };
    fetchData();
  }, []);

  useEffect(
    () => {
      const addCount = (op, key) => {
        const curSummary = summary[key];
        if (!curSummary) return op;
        const newOp = op.map(o => {
          const count = curSummary[o.value] || 0;
          return {...o, count};
        });

        Object.entries(curSummary).forEach(([key, val]) => {
          if (newOp.findIndex(x => x.value === key) === -1) {
            newOp.push({ label: key, value: key, count: val });
          }
        });

        newOp.sort(labelCompare);

        return newOp;
      };

      const newOptions = {};
      fields.forEach(f => {
        newOptions[f.name] = addCount(f.options, f.name);
      });
      setOptions(newOptions);
    },
    [summary]
  );

  const forceUpdate = () => {
    setRefresh(!refresh);
  };

  const updateProfile = async values => {
    try {
      const newProfile = calculateProfileUpdate(profile, values);

      await firestore()
        .collection("users")
        .doc(user.uid)
        .update(newProfile);

      if ("firstName" in newProfile || "lastName" in newProfile) {
        await user.updateProfile({
          displayName: `${newProfile.firstName ||
            profile.firstName} ${newProfile.lastName || profile.lastName}`
        });
        forceUpdate();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deleteUser = async (email, password) => {
    if (user.providerData[0].providerId === 'password') {
      const credential = auth.EmailAuthProvider.credential(
        email, password
      );
      await user.reauthenticateWithCredential(credential);
    } else {
      const provider = new auth.OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        tenant: "e9c554b7-2aec-4238-9b8e-372753d596ae"
      });

      try {
        await user.reauthenticateWithPopup(provider);
      } catch (error) {
        alert(error.message);
      }
    }
    try {
      await firestore()
        .collection("users")
        .doc(user.uid)
        .delete();
    } catch (err) {
      console.err(err);
    }
    await user.delete();

  };


  return (
    <UserContext.Provider
      value={{
        user,
        profile,
        updateProfile,
        summary,
        summaryLoading,
        forceUpdate,
        options,
        refresh,
        signInWithKvis,
        deleteUser,
        isSignInPending: signInPending.isPending,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserProvider, useUser, KVIS_SIGNIN_REDIRECT };
